/**
 * Vista em lista para pipelines — modo tabular de cards
 * Reutiliza dados de pipeline_cards, sincroniza com Kanban em tempo real
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Empty,
  Tooltip,
  Select,
  Input,
  Spin,
} from 'antd';
import { Edit, Search } from 'lucide-react';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { formatDate } from '@/lib/utils';
import { usePipelineBoard } from '../hooks/usePipelineBoard';
import { usePipelineTags } from '../hooks/usePipelineTags';
import { useProductionStartDay } from '@/hooks/useProductionStartDay';
import { useTransferredFromPipeline } from '../hooks/useTransferredFromPipeline';
import { PipelineCard } from '../types';
import { PipelineCardEditModal } from './PipelineCardEditModal';
import { matchesGrupoCota, searchMatchesGrupoOuCota } from '../utils/grupoCotaFilter';
import {
  PipelineBoardView,
  PipelineFilterSummary,
  isOutcomeStageName,
} from './PipelineFilterBar';

const EMPTY_TRANSFERRED_CARDS: PipelineCard[] = [];

interface PipelineListViewProps {
  pipelineId: string;
  search?: string;
  tagFilter?: string[];
  tenantFilter?: number[];
  assembleiaVencimentos?: string[];
  grupoFilter?: string;
  cotaFilter?: string;
  boardView?: PipelineBoardView;
  onSummaryChange?: (summary: PipelineFilterSummary) => void;
  productionPeriodFilter?: { month: number; year: number } | null;
  startDay?: number;
  onCardSelect?: (card: PipelineCard) => void;
}

const EMPTY_TAG_FILTER: string[] = [];
const EMPTY_TENANT_FILTER: number[] = [];
const EMPTY_ASSEMBLEIA_FILTER: string[] = [];

function matchesAssembleiaVencimento(
  dataVencimento: string | null | undefined,
  vencimentos: string[]
): boolean {
  if (vencimentos.length === 0) return true;
  if (!dataVencimento) return false;
  return vencimentos.includes(dataVencimento.slice(0, 10));
}

interface ListCard extends PipelineCard {
  stage_nome: string;
  stage_ordem: number;
  // Dados de oportunidade (grupo + bem)
  grupo_titulo?: string;
  bem_tipo?: string;
  cliente_nome?: string;
  contrato_valor?: number;
}

export function PipelineListView({
  pipelineId,
  search = '',
  tagFilter = EMPTY_TAG_FILTER,
  tenantFilter = EMPTY_TENANT_FILTER,
  assembleiaVencimentos = EMPTY_ASSEMBLEIA_FILTER,
  grupoFilter = '',
  cotaFilter = '',
  boardView = 'ativos',
  onSummaryChange,
  productionPeriodFilter,
  startDay: startDayProp,
  onCardSelect,
}: PipelineListViewProps) {
  const startDayFromTenant = useProductionStartDay();
  const startDay = startDayProp ?? startDayFromTenant;
  const { kanbanBoard, loading, error } = usePipelineBoard(
    pipelineId,
    productionPeriodFilter,
    startDay
  );
  const { tagsById } = usePipelineTags();
  const isVendaHistoryView = boardView === 'venda';
  const {
    data: transferredCards = EMPTY_TRANSFERRED_CARDS,
    isLoading: loadingTransferred,
  } = useTransferredFromPipeline(pipelineId, isVendaHistoryView);
  const [selectedCard, setSelectedCard] = useState<ListCard | null>(null);
  const [stageFilter, setStageFilter] = useState<string[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 50,
  });

  // Converter dados do kanban para lista flat
  const listData: ListCard[] = useMemo(() => {
    if (isVendaHistoryView) {
      return transferredCards.map((card) => {
        const dadosCard = card.dados_card || {};
        const contrato = card.contratos;
        return {
          ...card,
          stage_nome: 'Venda',
          stage_ordem: 0,
          grupo_titulo:
            contrato?.grupos?.titulo ||
            (contrato?.grupos?.numero_grupo ? `Grupo ${contrato.grupos.numero_grupo}` : '') ||
            (dadosCard.grupo_titulo as string) ||
            '—',
          bem_tipo: (contrato?.bens?.tipo_bem || (dadosCard.bem_tipo as string) || '—'),
          cliente_nome:
            (contrato?.clientes?.nome ||
              (dadosCard.cliente_nome as string) ||
              (dadosCard.nome as string) ||
              '—'),
          contrato_valor: (contrato?.valor_bem || (dadosCard.valor as number)),
        };
      });
    }

    if (!kanbanBoard) return [];

    const matchesView = (stageNome: string, oculta: boolean | undefined) => {
      switch (boardView) {
        case 'fixados':
          return stageNome.toLowerCase().includes('fixado');
        case 'venda':
          return false;
        case 'perdidos': {
          const nome = stageNome.toLowerCase();
          return nome.includes('perdido') || nome.includes('perda');
        }
        case 'todos':
          return true;
        case 'ativos':
        default:
          return !isOutcomeStageName(stageNome, oculta);
      }
    };

    const allCards: ListCard[] = [];
    kanbanBoard.stages.forEach((stageWithCards) => {
      if (!matchesView(stageWithCards.stage.nome, stageWithCards.stage.oculta)) {
        return;
      }

      stageWithCards.cards.forEach((card) => {
        const dadosCard = card.dados_card || {};
        const contrato = card.contratos;
        const grupoTitulo = (contrato?.grupos?.titulo || (contrato?.grupos?.numero_grupo ? `Grupo ${contrato.grupos.numero_grupo}` : '') || (dadosCard.grupo_titulo as string) || '—');
        const bemTipo = (contrato?.bens?.tipo_bem || (dadosCard.bem_tipo as string) || '—');
        const clienteNome = (contrato?.clientes?.nome || (dadosCard.cliente_nome as string) || (dadosCard.nome as string) || '—');
        const contratoValor = (contrato?.valor_bem || (dadosCard.valor as number));

        allCards.push({
          ...card,
          stage_nome: stageWithCards.stage.nome,
          stage_ordem: stageWithCards.stage.ordem,
          grupo_titulo: grupoTitulo,
          bem_tipo: bemTipo,
          cliente_nome: clienteNome,
          contrato_valor: contratoValor,
        });
      });
    });

    return allCards;
  }, [kanbanBoard, boardView, isVendaHistoryView, transferredCards]);

  // Filtros: busca + stage
  const filteredData = useMemo(() => {
    let result = listData;

    // Filtro de busca
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter((card) => {
        const dados = card.dados_card || {};
        const cardTagIds = Array.isArray(dados.tags) ? (dados.tags as string[]) : [];
        return (
          String(dados.nome || '').toLowerCase().includes(term) ||
          String(dados.telefone || '').toLowerCase().includes(term) ||
          String(dados.interesse || '').toLowerCase().includes(term) ||
          cardTagIds.some((id) => (tagsById.get(id)?.nome || '').toLowerCase().includes(term)) ||
          searchMatchesGrupoOuCota(card, term)
        );
      });
    }

    // Filtro de stage
    if (stageFilter.length > 0) {
      result = result.filter((card) => stageFilter.includes(card.stage_nome));
    }

    // Filtro de tag
    if (tagFilter.length > 0) {
      result = result.filter((card) => {
        const cardTagIds = Array.isArray(card.dados_card?.tags) ? (card.dados_card.tags as string[]) : [];
        return tagFilter.some((id) => cardTagIds.includes(id));
      });
    }

    // Filtro de tenant (representação)
    if (tenantFilter.length > 0) {
      result = result.filter((card) => tenantFilter.includes(card.tenant_id));
    }

    // Filtro de vencimento da assembleia
    if (assembleiaVencimentos.length > 0) {
      result = result.filter((card) =>
        matchesAssembleiaVencimento(
          card.contratos?.grupos?.data_vencimento_grupo,
          assembleiaVencimentos
        )
      );
    }

    // Filtro de grupo e cota (pós-venda)
    if (grupoFilter.trim() || cotaFilter.trim()) {
      result = result.filter((card) => matchesGrupoCota(card, grupoFilter, cotaFilter));
    }

    return result.sort((a, b) => {
      // Ordenar por stage_ordem, depois por data de criação (mais recentes primeiro)
      if (a.stage_ordem !== b.stage_ordem) {
        return a.stage_ordem - b.stage_ordem;
      }
      return (
        new Date(b.data_criacao).getTime() -
        new Date(a.data_criacao).getTime()
      );
    });
  }, [
    listData,
    search,
    stageFilter,
    tagFilter,
    tenantFilter,
    assembleiaVencimentos,
    grupoFilter,
    cotaFilter,
    tagsById,
  ]);

  useEffect(() => {
    if (!onSummaryChange) return;
    onSummaryChange({
      count: filteredData.length,
      value: filteredData.reduce((sum, card) => sum + Number(card.contrato_valor ?? 0), 0),
    });
  }, [filteredData, onSummaryChange]);

  // Opções de stage para filtro
  const stageOptions = useMemo(() => {
    if (isVendaHistoryView) {
      return [
        {
          value: 'Venda',
          label: `Venda (${transferredCards.length})`,
        },
      ];
    }
    if (!kanbanBoard) return [];
    return kanbanBoard.stages
      .filter((swc) => {
        switch (boardView) {
          case 'fixados':
            return swc.stage.nome.toLowerCase().includes('fixado');
          case 'venda':
            return false;
          case 'perdidos': {
            const nome = swc.stage.nome.toLowerCase();
            return nome.includes('perdido') || nome.includes('perda');
          }
          case 'todos':
            return true;
          case 'ativos':
          default:
            return !isOutcomeStageName(swc.stage.nome, swc.stage.oculta);
        }
      })
      .map((swc) => ({
        value: swc.stage.nome,
        label: `${swc.stage.nome} (${swc.cards.length})`,
      }));
  }, [kanbanBoard, boardView, isVendaHistoryView, transferredCards.length]);

  const columns: ColumnsType<ListCard> = [
    {
      title: 'Oportunidade',
      key: 'oportunidade',
      width: '28%',
      render: (_, record: ListCard) => (
        <div className="flex flex-col gap-1">
          <span className="font-medium text-gray-900 dark:text-white">
            {record.grupo_titulo} - {record.bem_tipo}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Cliente: {record.cliente_nome}
          </span>
        </div>
      ),
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente_nome',
      key: 'cliente_nome',
      width: '18%',
      render: (clienteNome: string) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {clienteNome || '—'}
        </span>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'contrato_valor',
      key: 'contrato_valor',
      width: '14%',
      render: (valor: number) => (
        <span className="text-sm font-medium">
          {valor
            ? new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(valor)
            : '—'}
        </span>
      ),
      sorter: (a, b) => {
        const valA = a.contrato_valor || 0;
        const valB = b.contrato_valor || 0;
        return valA - valB;
      },
    },
    {
      title: 'Stage',
      dataIndex: 'stage_nome',
      key: 'stage_nome',
      width: '14%',
      render: (stage: string, record: ListCard) => {
        const colorMap: Record<number, string> = {
          1: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
          2: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200',
          3: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-200',
          4: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200',
          5: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200',
        };
        const bgClass = colorMap[record.stage_ordem] || 'bg-gray-100 text-gray-700';
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${bgClass}`}>
            {stage}
          </span>
        );
      },
    },
    {
      title: 'Atualizado em',
      dataIndex: 'data_ultima_atualizacao',
      key: 'data_ultima_atualizacao',
      width: '12%',
      render: (date: string) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatDate(date)}
        </span>
      ),
      sorter: (a, b) =>
        new Date(a.data_ultima_atualizacao).getTime() -
        new Date(b.data_ultima_atualizacao).getTime(),
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: '10%',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              type="text"
              size="small"
              icon={<Edit className="w-4 h-4" />}
              onClick={() => setSelectedCard(record)}
              className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (error) {
    return (
      <div className="p-6">
        <p className="text-red-600 text-sm">{error.message}</p>
      </div>
    );
  }

  if (loading || (isVendaHistoryView && loadingTransferred)) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  if (!kanbanBoard || listData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <Empty
          description="Nenhum card encontrado"
          style={{ marginBottom: 0 }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Toolbar */}
      <div className="flex gap-3 items-end flex-wrap">
        <Input
          prefix={<Search className="w-4 h-4 text-gray-400" />}
          placeholder="Buscar por nome, telefone ou interesse..."
          value={search}
          onChange={() => {
            // This will be handled by parent component
          }}
          disabled
          style={{ width: 300 }}
          size="middle"
        />

        <Select
          mode="multiple"
          placeholder="Filtrar por stage"
          value={stageFilter}
          onChange={setStageFilter}
          options={stageOptions}
          style={{ minWidth: 200 }}
          size="middle"
          maxTagCount="responsive"
        />

        <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
          {filteredData.length} card{filteredData.length !== 1 ? 's' : ''} de{' '}
          {listData.length}
        </span>
      </div>

      {/* Tabela */}
      <div className="flex-1 overflow-hidden">
        <Table<ListCard>
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            total: filteredData.length,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
          }}
          size="small"
          locale={{
            emptyText: (
              <Empty
                description="Nenhum card encontrado"
                style={{ marginBottom: 0 }}
              />
            ),
          }}
          scroll={{ x: true }}
          onRow={(record) => ({
            onClick: () => setSelectedCard(record),
            className: selectedCard?.id === record.id ? 'bg-blue-50 dark:bg-zinc-900/50' : '',
            style: {
              cursor: 'pointer',
            },
          })}
        />
      </div>

      {/* Modal de edição do card */}
      <PipelineCardEditModal
        card={selectedCard}
        pipelineId={pipelineId}
        onClose={() => setSelectedCard(null)}
        onSave={() => {
          // Recarregar dados (handled by hook)
        }}
      />
    </div>
  );
}

export default PipelineListView;
